import CloseButton from 'Components/Common/CloseButton';
import InstagramCaption from 'Components/Common/InstagramCaption';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Spinner } from 'reactstrap';
import { fetchBasicSocialPostThunk, fetchSocialPostThunk } from 'slices/influencer/thunk';
import { AppDispatch, RootState } from 'store';
import { Influencer, SocialAccount } from 'types/influencer';
import { formatNumber } from 'utils/helpers';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import userProfilePicture from 'assets/images/users/user-dummy-img.jpg';

interface SocialPostModalProps {
    isOpen: boolean;
    onClose: () => void;
    postId?: string;
    influencer?: Influencer;
    socialAccount?: SocialAccount;
    disableComments?: boolean;
}

const SocialPostModal: React.FC<SocialPostModalProps> = ({
    isOpen,
    onClose,
    postId,
    influencer,
    socialAccount,
}) => {
    const dispatch: AppDispatch = useDispatch();

    const postFromAccount = useSelector(
        (state: RootState) => state.influencers.social_posts[socialAccount?.uuid ?? '']?.[postId ?? '']?.post
    );
    const postFromBasic = useSelector(
        (state: RootState) => state.influencers.basic_social_posts[postId ?? '']?.post
    );
    const postLoading = useSelector((state: RootState) => state.influencers.postLoading);
    const post = postFromAccount ?? postFromBasic;

    useEffect(() => {
        if (!isOpen || !postId) return;
        if (socialAccount?.uuid) {
            dispatch(fetchSocialPostThunk({ socialAccountId: socialAccount.uuid, postId }));
        } else {
            dispatch(fetchBasicSocialPostThunk({ postId }));
        }
    }, [dispatch, isOpen, postId, socialAccount?.uuid]);

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg" className="post-modal">
            <CloseButton top="0" onClose={onClose} />
            {postLoading && !post && (
                <div className="d-flex justify-content-center p-5">
                    <Spinner />
                </div>
            )}
            {postId && post && (
                <div className="post-modal__body d-flex flex-column flex-lg-row gap-2 gap-lg-0">
                    {post.platform === 'ig' && post.ig && (
                        <>
                            {post.ig.media_url ? (
                                <>
                                    {post.ig.media_type === 'VIDEO' && (
                                        <video
                                            src={post.ig.media_url}
                                            controls
                                            autoPlay
                                            className="post"
                                            controlsList="nodownload nofullscreen noremoteplayback"
                                        />
                                    )}
                                    {(post.ig.media_type === 'IMAGE' || post.ig.media_type === 'CAROUSEL_ALBUM') && (
                                        <img src={post.ig.media_url} className="post" alt="" />
                                    )}
                                </>
                            ) : (
                                <div className="no-media-cta">
                                    <button type="button" className="btn btn-gradient gap-2">
                                        <span className="mdi mdi-open-in-new fs-4" />
                                        View on Instagram
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                    {post.platform === 'yt' && post.yt && post.type === 'VIDEO' && (
                        <iframe
                            src={
                                post.yt.permalink
                                    ? `https://www.youtube.com/embed/${post.yt.permalink.split('/').pop()?.split('?')[0]}`
                                    : ''
                            }
                            className="mw-100 mh-800px align-self-center"
                            style={{ objectFit: 'contain', width: '100%', height: '740px', maxHeight: '75vh' }}
                            frameBorder="0"
                            allowFullScreen
                            title="YouTube video"
                        />
                    )}
                    <div className="post-details">
                        <div className="d-flex flex-column gap-3">
                            <div className="d-flex gap-2 justify-content-between align-items-center">
                                <div className="post-profile d-flex gap-3 p-4 pb-0">
                                    <img
                                        src={socialAccount?.profile_picture_url ?? userProfilePicture}
                                        className="post-influencer-pic"
                                        alt=""
                                    />
                                    <div className="name-and-date d-flex flex-column align-items-start">
                                        <span className="influencer-name text-truncate">
                                            {influencer?.name ?? socialAccount?.name ?? `@${socialAccount?.handle}`}
                                        </span>
                                        <span className="influencer-address">{post.posted_on}</span>
                                    </div>
                                </div>
                                {post.ig?.permalink && (
                                    <a
                                        title="View on Instagram"
                                        className="p-4 pb-0"
                                        href={post.ig.permalink}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <span className="mdi mdi-open-in-new fs-4" />
                                    </a>
                                )}
                            </div>
                            <PerfectScrollbar options={{ suppressScrollX: true }} style={{ height: '600px' }}>
                                <div className="d-flex flex-column gap-3 p-4 pt-0">
                                    <InstagramCaption
                                        text={
                                            post.platform === 'ig'
                                                ? (post.ig?.caption ?? '')
                                                : post.platform === 'yt'
                                                  ? (post.yt?.title ?? '')
                                                  : ''
                                        }
                                    />
                                    <div className="divider mt-n2" />
                                    <div className="post-modal-stats d-flex align-items-center justify-content-start gap-3 gap-md-2">
                                        <div className="d-flex align-items-center gap-1" title="Likes">
                                            <span className="mdi mdi-heart-outline fs-4" />
                                            {formatNumber(post.ig?.like_count ?? 0)}
                                        </div>
                                        <div className="d-flex align-items-center gap-1" title="Comments">
                                            <span className="mdi mdi-comment-outline fs-4" />
                                            {formatNumber(post.ig?.comments_count ?? 0)}
                                        </div>
                                        <div className="d-flex align-items-center gap-1" title="Views">
                                            <span className="mdi mdi-eye-outline fs-4" />
                                            {formatNumber(post.ig?.views_count ?? 0)}
                                        </div>
                                        <div className="d-flex align-items-center gap-1" title="Shares">
                                            <span className="mdi mdi-share-outline fs-4" />
                                            {formatNumber(post.ig?.shares_count ?? 0)}
                                        </div>
                                        <div className="d-flex align-items-center gap-1" title="Reach">
                                            <span className="mdi mdi-chart-bar fs-4" />
                                            {formatNumber(post.ig?.reach_count ?? 0)}
                                        </div>
                                    </div>
                                </div>
                            </PerfectScrollbar>
                        </div>
                    </div>
                </div>
            )}
            {!postLoading && (!postId || !post) && <span className="p-4">No media found!</span>}
        </Modal>
    );
};

export default SocialPostModal;
